import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDaoConfig } from '@/hooks/useDaoConfig';

import { animateElement, type AnimationType } from './../animations';
import type { WelcomeStepRef } from './types';

export const WelcomeStep5 = forwardRef<WelcomeStepRef>((_, ref) => {
  const { daoName } = useDaoConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-in]');

    elements?.forEach((element) => {
      const animation = element.dataset.animateIn as AnimationType | undefined;

      if (animation) {
        animateElement(element, animation).catch(() => {});
      }
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateOut: async () => {
        const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-out]');

        if (!elements) return;

        await Promise.all(
          Array.from(elements).map((element) => {
            const animation = element.dataset.animateOut as AnimationType | undefined;

            if (!animation) return Promise.resolve();

            return animateElement(element, animation);
          }),
        );
      },
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-[--spacing-12] items-center justify-center text-center text-balance"
    >
      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut">
        What makes {daoName} different.
      </h1>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="text-[--text-dark-secondary] font-normal"
      >
        {daoName} runs on blockchain. Every action here is recorded in a way no one can edit afterwards,
        not even {daoName}. That's what makes the trust mechanics from the previous steps work.
      </p>

      {/* Image - animations container */}
      <div className="animationsContainer mt-40">
        <div className="relative">
          <div
            data-animate-in="scaleUp4"
            data-animate-out="scaleDown4"
            className="h-[167px] translate-y-4 aspect-square rounded-full glassEffect overflow-visible border-0 opacity-0"
          >
            <img
              src="./public/images/onboarding/welcomeStep5_circle.svg"
              // alt="gradient border circle"
            />
          </div>

          {/* Blue bubble - top left */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-10 absolute opacity-0
              -top-[13%] -left-[13%] -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square max-w-[37px]
              rounded-full bg-[--colors-cyan-600]
            "
          ></div>

          {/* Green bubble - bottom right */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-10 absolute opacity-0
              top-[113%] left-[113%] -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square 
              rounded-full bg-[--colors-emerald-500]
            "
          ></div>

          {/* Text box */}
          <div
            data-animate-in="fadeIn"
            className="absolute z-50 top-0 -translate-y-[55%] left-[100%] translate-x-[44px] w-[360px] object-contain rounded-[--radi-7] glassEffect border-0 opacity-0"
          >
            <img src="./public/images/onboarding/welcomeStep5_textbox.svg" alt="" />
          </div>
        </div>
      </div>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="text-[--text-dark-secondary] font-normal -mt-20"
      >
        It's also a DAO: a decentralised autonomous organisation. Decisions about how the platform
        works aren't made by a company. They're made by the members themselves.
      </p>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="text-[--text-dark-secondary] font-normal"
      >
        Do you want to know more?
        <span
          data-animate-in="textFadeIn"
          data-animate-out="textFadeOut"
          className="ml-2 text-[--state-brand-active] hover:underline"
        >
          Read our FAQs
        </span>
      </p>
    </div>
  );
});

WelcomeStep5.displayName = 'WelcomeStep5';
